import { useState, useCallback } from 'react'
import { useUpdateImplementation } from '../api'

export interface UseImplementationReturn {
  content: string
  isDirty: boolean
  isLoading: boolean
  updateContent: (newContent: string) => void
  loadFromServer: (serverContent: string) => void
  save: () => Promise<void>
  clearPendingChanges: () => void
}

export const useImplementation = (translationMapId: string): UseImplementationReturn => {
  const [content, setContent] = useState('')
  const [isDirty, setIsDirty] = useState(false)
  const [lastSavedContent, setLastSavedContent] = useState('')
  const [serverContent, setServerContent] = useState('')

  const updateImplementation = useUpdateImplementation()

  // Load content from server (decoded)
  const loadFromServer = useCallback((serverContent: string) => {
    try {
      const decoded = serverContent ? atob(serverContent) : ''
      setContent(decoded)
      setIsDirty(false)
      setLastSavedContent(decoded)
      setServerContent(decoded) // Store the original server content
    } catch (error) {
      console.error('Error decoding server content:', error)
      setContent('')
      setIsDirty(false)
      setLastSavedContent('')
      setServerContent('')
    }
  }, [])

  // Update content (user edit)
  const updateContent = useCallback(
    (newContent: string) => {
      setContent(newContent)

      // Smart comparison: only set dirty if content is actually different from server content
      const isActuallyDirty = newContent !== serverContent
      setIsDirty(isActuallyDirty)
    },
    [serverContent]
  )

  // Save to server
  const save = useCallback(async () => {
    if (!isDirty || !translationMapId) return

    try {
      const encoded = btoa(content)
      await updateImplementation.mutateAsync({
        id: translationMapId,
        encoded_implementation: encoded,
      })

      // Reset state after successful save
      setIsDirty(false)
      setLastSavedContent(content)
      setServerContent(content) // Update server content to current content
    } catch (error) {
      console.error('Failed to save implementation:', error)
      throw error
    }
  }, [content, isDirty, translationMapId, updateImplementation])

  // Clear pending changes
  const clearPendingChanges = useCallback(() => {
    setIsDirty(false)
    setContent(serverContent) // Reset to server content
  }, [serverContent])

  return {
    content,
    isDirty,
    isLoading: updateImplementation.isLoading,
    updateContent,
    loadFromServer,
    save,
    clearPendingChanges,
  }
}
